import React, { useState, type JSX } from 'react';

import { List, Radio, Stack, ListItem, Typography, RadioGroup, FormHelperText, FormControlLabel } from '@mui/material';
import { useConfirm } from '@fiji/common/src/hooks';
import { CustomModal, CustomTransComponent } from '../../../components';
import { ModalActions } from '../../../components/CustomModal';
import { useHandleAddWidgets } from './hooks';
import { resetWidget, setPayload } from '@fiji/common/src/features/widgetManagement/widgetSlice';
import { useAppDispatch, useTypedSelector } from '@fiji/common/src/app/store';
import { FormSkeleton } from './Skeletons';
import { handleWhiteSpaces } from 'utils/helpers';
import { selectCurrentPermission } from '@fiji/common/src/features/profile/profileSlice';
import { useRBAC, useTextFieldError } from 'hooks';
import LanguageTextField from 'components/FormElements/LanguageTextField';

export const NewWidgetModal = React.forwardRef((_props, ref): JSX.Element => {
    const dispatch = useAppDispatch();
    const payload = useTypedSelector((state) => state.widget?.payload);
    const { widgetCreationLoader: isLoading, handleSubmit, widgetError } = useHandleAddWidgets();
    const [errorMsg, setErrorMsg] = useState<any>(null);
    const { hasTextError, makeErrorHandler } = useTextFieldError();

    const handleModalAction = (action: boolean): void => {
        if (action) {
            onClick();
            return;
        }
        onCancel();
    };

    React.useImperativeHandle(
        ref,
        () => ({
            handleModalAction: (action: any): void => handleModalAction(action),
        }),
        []
    );

    React.useEffect(() => {
        if (widgetError?.data?.errorMessage) {
            setErrorMsg(widgetError?.data?.errorMessage?.[0]);
        }
    }, [widgetError]);
    const { isVisible, onCancel, onClick, onConfirm } = useConfirm((): Promise<void> => handleSubmit(onCancel));

    const permissions = useTypedSelector(selectCurrentPermission);

    const { hasPermission } = useRBAC(permissions);
    const canCreateSystemWidgets = hasPermission('widget-management_create-system-widgets');
    const canCreateUserWidgets = hasPermission('widget-management_view-user-widgets');

    const widgetName = payload?.name ?? '';
    const description = payload?.description ?? '';
    // Pre-computed once per render; avoids three repeated optional-chain branches in JSX.
    const lowerErrorMsg = errorMsg?.toLowerCase?.() ?? '';
    const isSaveDisabled = !widgetName || !handleWhiteSpaces(widgetName) || isLoading || hasTextError;
    const saveLabel = isLoading ? (
        <CustomTransComponent translationKey={'COMMON:SAVING'} />
    ) : (
        <CustomTransComponent translationKey={'COMMON:SAVE_LABEL'} />
    );

    const newWidgetModalActions: ModalActions[] = [
        {
            key: '#creationcancel',
            testId: 'new-add-widget-cancel-button',
            label: <CustomTransComponent translationKey={'COMMON:CANCEL'} />,
            close: true,
            variant: 'outlined',
            handleClick: (): void => {
                onCancel();
                dispatch(resetWidget());
            },
        },
        {
            key: '#creationsave',
            testId: 'new-add-widget-confirm-button',
            label: saveLabel,
            variant: 'contained',
            disabled: isSaveDisabled,
            isLoading: isLoading,
            handleClick: (): void => {
                onConfirm();
            },
        },
    ];

    return (
        <CustomModal
            key="#new!@1313d"
            type="primary"
            actionsDivider
            header={<CustomTransComponent translationKey={'WIDGETS:ADD_WIDGET'} />}
            actions={newWidgetModalActions}
            isOpen={isVisible}
            onClose={onCancel}
            data-testid="new-add-widget-modal"
        >
            {isLoading && <FormSkeleton />}
            {!isLoading && (
                <>
                    <Typography variant="subtitle1" fontWeight={'600'} my={2}>
                        <CustomTransComponent translationKey={'WIDGETS:CLONE_MODAL_HEADER'} />
                    </Typography>
                    <LanguageTextField
                        data-testid="new-add-widget-name-input"
                        fullWidth
                        id="name"
                        name="name"
                        label={<CustomTransComponent translationKey={'COMMON:NAME'} />}
                        value={widgetName}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                            if (errorMsg?.length) {
                                setErrorMsg(null);
                            }
                            dispatch(setPayload({ key: e.target.name, value: e.target.value }));
                        }}
                        variant="filled"
                        inputProps={{ maxLength: 32 }}
                        className="margin-top-18"
                        required
                        treatLeadingWhitespaceAsError
                        handleTextError={makeErrorHandler('name')}
                    />
                    <Stack direction={'row'} justifyContent={'space-between'} px={1}>
                        {lowerErrorMsg.includes('name') ? (
                            <FormHelperText error={Boolean(errorMsg)}>{errorMsg}</FormHelperText>
                        ) : (
                            <FormHelperText>
                                <CustomTransComponent translationKey={'COMMON:REQUIRED_LABEL'} />
                            </FormHelperText>
                        )}
                        <FormHelperText>{`${widgetName.length}/32`}</FormHelperText>
                    </Stack>
                    <LanguageTextField
                        data-testid="new-add-widget-description-input"
                        id="description"
                        fullWidth
                        variant="filled"
                        name="description"
                        value={description}
                        inputProps={{ maxLength: 128 }}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                            dispatch(setPayload({ key: e.target.name, value: e.target.value }));
                        }}
                        label={<CustomTransComponent translationKey={'ALARM_DETAILS:DESCRIPTION_TITLE'} />}
                        className="margin-top-18"
                        treatLeadingWhitespaceAsError
                        handleTextError={makeErrorHandler('description')}
                    />
                    <Stack direction={'row'} justifyContent={'space-between'} px={1}>
                        {lowerErrorMsg.includes('description') ? (
                            <FormHelperText error={Boolean(errorMsg)}>{errorMsg ?? ''}</FormHelperText>
                        ) : (
                            <FormHelperText></FormHelperText>
                        )}
                        <FormHelperText className="text-align-end padding-x-16">{`${description.length}/128`}</FormHelperText>
                    </Stack>
                    {canCreateSystemWidgets && canCreateUserWidgets && (
                        <>
                            <Typography variant="subtitle1" fontSize={'16px'} fontWeight={'600'} mt={'18px'}>
                                <CustomTransComponent
                                    translationKey={'NEW_WIDGET_MODAL:IS_THIS_A_SYSTEM_OR_USER_WIDGET'}
                                />
                            </Typography>
                            <List>
                                <RadioGroup
                                    data-testid="new-add-widget-creation-modal-radio"
                                    id="creationmodalradio"
                                    defaultValue={payload.isSystemWidget ?? false}
                                    onChange={(e: any): any => {
                                        dispatch(
                                            setPayload({
                                                key: 'isSystemWidget',
                                                value: Boolean(e.target.value !== 'false'),
                                            })
                                        );
                                    }}
                                >
                                    <ListItem>
                                        <FormControlLabel
                                            data-testid="new-add-widget-system-widget-radio"
                                            value={true}
                                            control={<Radio />}
                                            id="system"
                                            label={
                                                <Typography variant="body1">
                                                    <span className="f-16 fw-600 black-500">
                                                        <CustomTransComponent
                                                            translationKey={'NEW_WIDGET_MODAL:SYSTEM_WIDGET'}
                                                        />{' '}
                                                    </span>{' '}
                                                    <CustomTransComponent
                                                        translationKey={
                                                            'DASHBOARDS:CLONE_MODAL_DASHBOARD.SYSTEM_DASHBOARD_DESCRIPTION'
                                                        }
                                                    />
                                                </Typography>
                                            }
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <FormControlLabel
                                            data-testid="new-add-widget-user-widget-radio"
                                            value={false}
                                            id="user"
                                            control={<Radio />}
                                            label={
                                                <Typography variant="body1">
                                                    <span className="f-16 fw-600 black-500">
                                                        <CustomTransComponent
                                                            translationKey={'NEW_WIDGET_MODAL:USER_WIDGET'}
                                                        />{' '}
                                                    </span>{' '}
                                                    <CustomTransComponent
                                                        translationKey={
                                                            'NEW_WIDGET_MODAL:PRIVATE_DISPLAYED_ONLY_TO_YOU'
                                                        }
                                                    />
                                                </Typography>
                                            }
                                        />
                                    </ListItem>
                                </RadioGroup>
                            </List>
                        </>
                    )}
                </>
            )}
        </CustomModal>
    );
});
